import { Icons, type LBIcon } from '@/assets/icons/icons';
import { type ModalFC } from '@/components/modals/modalsManager';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { Grid, XStack, YStack } from '@/components/ui/layout/layout';
import { Text } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { navigate } from '@/utils/navigation-service';

export const SPRITE_CREATE_DESCRIPTION =
  'Turn your ideas and recent work into something you can use and share.';

export const SPRITE_FROM_SCRATCH_PROMPT = 'Help me build something new';

interface SpriteCreateOption {
  label: string;
  description: string;
  prompt: string;
  icon: LBIcon;
}

/** Starting points the user picks; each opens a new chat with the matching prefilled prompt. */
const SPRITE_CREATE_OPTIONS: SpriteCreateOption[] = [
  {
    label: 'Interactive quiz',
    description: 'Test yourself on what you’ve been learning.',
    prompt:
      'Build an interactive quiz based on topics I have been learning about recently. Let me answer one question at a time, explain each answer, and show my score at the end.',
    icon: Icons.brain,
  },
  {
    label: 'Personal website',
    description: 'Showcase your work and interests.',
    prompt:
      'Can you build a personal website for me based on everything you know about me',
    icon: Icons.www,
  },
  {
    label: 'Project tracker',
    description: 'Organize milestones and next steps.',
    prompt:
      'Create a project tracker spreadsheet for a project I have been working on recently, with milestones, tasks, owners, due dates, and status. Use details from my recent work and leave anything unknown blank.',
    icon: Icons.table,
  },
  {
    label: 'Visual explainer',
    description: 'Make a complex topic easier to grasp.',
    prompt:
      'Build a visual explainer for a complicated topic I have been reading about or dealing with recently.',
    icon: Icons.insights,
  },
];

/**
 * Open a new chat with the prompt prefilled. The `/chats` route prefills the new-chat composer's
 * draft from `auto_prompt` on mount, so this is race-free (no dependency on the composer being
 * mounted first). Uses the navigation service rather than `useNavigate` because this is also
 * invoked from the create modal, which `ModalsManager` renders outside the router's React tree.
 */
function startSpriteChat(prompt: string): void {
  navigate({ to: '/chats', search: { auto_prompt: prompt } });
}

export function SpriteCreateOptions({
  onSelected,
}: {
  onSelected?: () => void;
}) {
  const selectPrompt = (prompt: string) => {
    startSpriteChat(prompt);
    onSelected?.();
  };

  return (
    <YStack className="w-full items-center gap-6">
      <Button onClick={() => selectPrompt(SPRITE_FROM_SCRATCH_PROMPT)}>
        <Icons.plus aria-hidden="true" />
        Start from scratch
      </Button>
      <YStack className="w-full gap-3">
        <Text size="xs" variant="muted" className="text-center">
          Or start with an idea
        </Text>
        <Grid className="grid-cols-1 gap-3 sm:grid-cols-2">
          {SPRITE_CREATE_OPTIONS.map((option) => (
            <button
              key={option.label}
              type="button"
              onClick={() => selectPrompt(option.prompt)}
              className={cn(
                'rounded-xl border border-border bg-surface px-4 py-3 text-left',
                'hover:border-border-strong hover:bg-surface-hover active:bg-surface-active',
                'transition-colors focus-ring'
              )}
            >
              <YStack className="gap-2">
                <XStack className="items-center gap-2">
                  <option.icon
                    aria-hidden="true"
                    className="size-4 shrink-0 text-foreground-muted"
                  />
                  <span className="text-sm font-medium text-foreground">
                    {option.label}
                  </span>
                </XStack>
                <span className="text-xs/relaxed text-balance text-foreground-muted">
                  {option.description}
                </span>
              </YStack>
            </button>
          ))}
        </Grid>
      </YStack>
    </YStack>
  );
}

export const CreateSpriteModal: ModalFC<{}> = ({ open, onOpenChange }) => (
  <Dialog.Root open={open} onOpenChange={onOpenChange}>
    <Dialog.Content className="gap-6">
      <YStack className="items-center gap-2 px-4 pt-2 text-center">
        <Dialog.Title className="text-xl font-serif leading-snug">
          Create a new file
        </Dialog.Title>
        <Dialog.Description className="max-w-sm text-balance leading-relaxed">
          {SPRITE_CREATE_DESCRIPTION}
        </Dialog.Description>
      </YStack>
      <SpriteCreateOptions onSelected={() => onOpenChange(false)} />
    </Dialog.Content>
  </Dialog.Root>
);
